// PfEntity_Shareholders.jsx
import React, { useState } from "react";

export default function PfEquity_Shareholders({
  shareholders,
  setShareholders,
  totalShares = 0
}) {
  const safeShareholders = Array.isArray(shareholders) ? shareholders : [];

  const roleOptions = [
    "Founder",
    "Co-Founder",
    "Executive Director",
    "Non-Executive Director",
    "Independent Director",
    "Angel Investor",
    "Venture Capital (VC)",
    "ESOP"
  ];

  const [modalVisible, setModalVisible] = useState(false);
  const [removeIndex, setRemoveIndex] = useState(null);

  // ---------------------------
  // Add Shareholder
  // ---------------------------
  const addShareholder = () => {
    const usedShares = safeShareholders.reduce(
      (s, sh) => s + Number(sh.shares || 0),
      0
    );

    if (usedShares >= totalShares) {
      alert("❌ No remaining shares available");
      return;
    }

    setShareholders([
      ...safeShareholders,
      { name: "", role: "", shares: "", holdingPercent: "" }
    ]);
  };

  // ---------------------------
  // Update Logic
  // ---------------------------
  const updateShareholder = (index, field, rawValue) => {
    const updated = safeShareholders.map((s) => ({ ...s }));
    const cur = updated[index];
    const ts = Number(totalShares);

    if (field === "shares") {
      const shares = rawValue === "" ? "" : Number(rawValue);
      cur.shares = shares;
      cur.holdingPercent =
        shares === "" || !ts ? "" : Number(((shares / ts) * 100).toFixed(2));
    } else if (field === "holdingPercent") {
      const pct = rawValue === "" ? "" : Number(rawValue);
      cur.holdingPercent = pct;
      cur.shares = pct === "" || !ts ? "" : Math.round((pct / 100) * ts);
    } else {
      cur[field] = rawValue;
    }

    const usedShares = updated.reduce(
      (sum, s) => sum + Number(s.shares || 0), 0);
    const usedPercent = updated.reduce(
      (sum, s) => sum + Number(s.holdingPercent || 0), 0);

    if (usedShares > ts || usedPercent > 100) {
      alert("❌ Allocation exceeds available shares or 100%");
      return;
    }

    setShareholders(updated);
  };

  // ---------------------------
  // Delete Confirmation (Modal)
  // ---------------------------
  const askRemove = (index) => {
    setRemoveIndex(index);
    setModalVisible(true);
  };

  const confirmRemove = () => {
    if (removeIndex !== null) {
      setShareholders(safeShareholders.filter((_, i) => i !== removeIndex));
    }
    closeModal();
  };

  const closeModal = () => {
    setModalVisible(false);
    setRemoveIndex(null);
  };

  // Totals
  const totalSharesUsed = safeShareholders.reduce(
    (s, sh) => s + Number(sh.shares || 0), 0);
  const totalPercentUsed = safeShareholders.reduce(
    (s, sh) => s + Number(sh.holdingPercent || 0), 0);
  const remainingShares = totalShares - totalSharesUsed;

  return (
    <div style={{ marginTop: 20 }}>
      



      <h4>Shareholders</h4>

      {/* TABLE */}
      <table className="w3-table-all">
        <tbody>
          {safeShareholders.map((s, i) => (
            <tr key={i}>
              <td>
                <input
                  className="w3-input w3-border"
                  placeholder="Name"
                  value={s.name}
                  onChange={(e) => updateShareholder(i, "name", e.target.value)}
                />
              </td>
              <td>
                <select
                  className="w3-input w3-border"
                  value={s.role}
                  onChange={(e) => updateShareholder(i, "role", e.target.value)}
                >
                  <option value="">Select Role</option>
                  {roleOptions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ width: 120 }}>
                <input
                  className="w3-input w3-border"
                  type="number"
                  placeholder="Shares"
                  value={s.shares}
                  min="0"
                  onChange={(e) =>
                    updateShareholder(i, "shares", e.target.value)
                  }
                />
              </td>
              <td style={{ width: 120 }}>
                <input
                  className="w3-input w3-border"
                  type="number"
                  placeholder="%"
                  value={s.holdingPercent}
                  min="0"
                  max="100"
                  onChange={(e) =>
                    updateShareholder(i, "holdingPercent", e.target.value)
                  }
                />
              </td>
              <td style={{ width: 40 }}>
                <button
                  type="button"
                  className="w3-button w3-red"
                  onClick={() => askRemove(i)}
                >
                  X
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Add Button */}
      <div style={{ marginTop: 10 }}>
        <button
          type="button"
          onClick={addShareholder}
          disabled={remainingShares <= 0}
          className={`w3-button ${remainingShares <= 0 ? "w3-red" : "w3-indigo"}`}
        >
          + Add Shareholder
        </button>

        <span style={{ marginLeft: 15 }}>
          ({remainingShares}) shares available
        </span>
      </div>

      {/* Totals */}
      <table className="w3-table-all" style={{ marginTop: 20 }}>
        <tbody>
          <tr>
            <th>Total Shares Allocated</th>
            <th>Total %</th>
            <th>Remaining Shares</th>
          </tr>
          <tr>
            <td>{totalSharesUsed}</td>
            <td>{totalPercentUsed.toFixed(2)}%</td>
            <td>{remainingShares}</td>
          </tr>
        </tbody>
      </table>

      {/* ---------------------------------- */}
      {/* W3 Modal (Confirmation)             */}
      {/* ---------------------------------- */}
      {modalVisible && (
        <div className="w3-modal" style={{ display: "block" }}>
          <div className="w3-modal-content w3-animate-top w3-card-4" style={{ maxWidth: 400 }}>
            <header className="w3-container w3-red">
              <span
                onClick={closeModal}
                className="w3-button w3-display-topright"
              >
                ×
              </span>
              <h5>Confirm Delete</h5>
            </header>

            <div className="w3-container" style={{ padding: 20 }}>
              <p>
                Are you sure you want to remove
                <b> {safeShareholders[removeIndex]?.name || "this shareholder"}?</b>
              </p>
            </div>

            <footer className="w3-container w3-padding w3-light-grey" style={{ textAlign: "right" }}>
              <button className="w3-button w3-red" onClick={confirmRemove}>
                Yes, Remove
              </button>
              <button
                className="w3-button w3-gray"
                style={{ marginLeft: 8 }}
                onClick={closeModal}
              >
                Cancel
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
